import { Session } from "./session";
import { Artichoke } from "../artichoke/artichoke";
import { ArtichokeApi } from "../artichoke/artichoke-api";
import { LoggerFactory } from "../logger/logger-factory";
import type { Config } from "../config/config";
import { HttpClient } from "../http/http-client";
import { UUIDGenerator } from "../utils/uuid-generator";
import { XMLHttpRequestFactory } from "../http/xml-http-request-factory";
import { RoomFactory } from "../rooms/room-factory";
import { CallFactory } from "../calls/call-factory";
import { WebRTCStats } from "../rtc/stats/webrtc-stats";
import { RTCPoolRepository } from "../rtc/rtc-pool-repository";
import { RecordingRTCPoolRepository } from "../rtc/recording-rtc-pool-repository";
import { RecordingRTCPoolFactory } from "../rtc/recording-rtc-pool-factory";
import { MediaTrackOptimizer } from "../rtc/media-track-optimizer";
import { RTCPoolFactory } from "../rtc/rtc-pool-factory";
import { Spinner } from "../spinner/spinner";
import { SpinnerApi } from "../spinner/spinner-api";
import type { ID } from "../protocol/protocol";
import { SocketIOClient } from "../http/socketio-client";
import { io } from "socket.io-client";

export class SessionFactory {
  constructor(
    private readonly config: Config,
    private readonly loggerFactory: LoggerFactory
  ) {}

  public create(id: ID): Session {
    return new Session(
      this.createArtichoke(id),
      this.createSpinner()
    );
  }

  private createSpinner(): Spinner {
    return new Spinner(
      new SpinnerApi(
        new HttpClient(
          this.loggerFactory.create("Spinner HttpClient"),
          new URL(this.config.spinner.apiPath, this.config.spinner.server),
          new XMLHttpRequestFactory()
        )
      )
    );
  }

  private createArtichoke(id: ID): Artichoke {
    const artichokeApi = this.createArtichokeApi(id);

    return new Artichoke(
      artichokeApi,
      this.createCallFactory(artichokeApi),
      new RoomFactory(this.loggerFactory, artichokeApi),
      this.loggerFactory.create("Artichoke"),
      this.config.artichoke.heartbeatTimeoutMultiplier,
      this.config.artichoke.fallbackReconnectDelayMs
    );
  }

  private createArtichokeApi(id: ID): ArtichokeApi {
    return new ArtichokeApi(
      id,
      this.createSocketIOClient(),
      this.createHttpClient()
    );
  }

  private createHttpClient(): HttpClient {
    const httpClientLogger = this.loggerFactory.create("Artichoke HttpClient");
    const httpApiUrl = new URL(
      this.config.artichoke.apiPath,
      this.config.artichoke.server
    );

    const xmlHttpRequestFactory = new XMLHttpRequestFactory();

    return new HttpClient(
      httpClientLogger,
      httpApiUrl,
      xmlHttpRequestFactory
    );
  }

  private createSocketIOClient() {
    return new SocketIOClient(
      io(),
      new UUIDGenerator(),
      this.config.artichoke.askTimeoutMs
    );
  }

  private createCallFactory(artichokeApi: ArtichokeApi): CallFactory {
    const webrtcStats = new WebRTCStats();

    const rtcPoolFactory = new RTCPoolFactory(
      this.config.rtc,
      this.loggerFactory,
      this.loggerFactory.create("RTCPoolFactory"),
      artichokeApi,
      webrtcStats
    );

    const rtcPoolRepository = new RTCPoolRepository(
      this.loggerFactory.create("RTCPoolRepository"),
      rtcPoolFactory
    );

    const recordingRTCPoolFactory = new RecordingRTCPoolFactory(
      this.config.rtc,
      this.config.artichoke,
      this.loggerFactory,
      this.loggerFactory.create("RecordingRTCPoolFactory"),
      artichokeApi,
      webrtcStats
    );

    const recordingRTCPoolRepository = new RecordingRTCPoolRepository(
      this.loggerFactory.create("RecordingRTCPoolRepository"),
      recordingRTCPoolFactory
    );

    return new CallFactory(
      this.loggerFactory,
      artichokeApi,
      rtcPoolRepository,
      recordingRTCPoolRepository,
      new MediaTrackOptimizer(
        this.config.rtc,
        this.loggerFactory.create("MediaTrackOptimizer")
      )
      // janusService,
    );
  }
}
